import { css, StyleSheet } from 'aphrodite';
import { type FC } from 'react';

import type { ResourceVisibility } from '../../../extViewmodels';
import { SettingsIcon } from '../../../web/components/svgs/icons/SettingsIcon';
import { Button } from '../../../web/components-v2/Button';
import { Checkbox } from '../../../web/components-v2/Checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../../../web/components-v2/Popover';
import { THEME } from '../../../web/components-v2/Theme/theme';

export type AdvancedSettingsPopoverProps = {
	/** "Save a copy of this email as a note". */
	saveAsNote: boolean;
	onSaveAsNoteChange: (next: boolean) => void;
	/** Note visibility: `'all'` (shared) or `'admin'` (private). */
	noteVisibility: ResourceVisibility;
	onNoteVisibilityChange: (next: ResourceVisibility) => void;
	disabled?: boolean;
};

/**
 * The composer's "Advanced Settings" gear popover. Scoped to the two controls
 * the focused-view composer surfaces (per design): "Save a copy of this email as
 * a note" and the "Shared note" visibility. A modern, react-query-free stand-in
 * for the legacy MobX `EmailComposerOptions`; the two values map directly to
 * `useSendEmail`'s `saveAsNote` + `noteVisibility`.
 *
 * Note: components-v2 has no binary Switch primitive, so "Shared note" is a
 * Checkbox (checked = shared/`'all'`, unchecked = private/`'admin'`) — functional
 * parity with the legacy toggle.
 */
export const AdvancedSettingsPopover: FC<AdvancedSettingsPopoverProps> = ({
	saveAsNote,
	onSaveAsNoteChange,
	noteVisibility,
	onNoteVisibilityChange,
	disabled = false,
}) => {
	return (
		<Popover>
			<PopoverTrigger asChild>
				<Button variant='ghost' styles={[styles.trigger]} disabled={disabled}>
					<SettingsIcon className={css(styles.gear)} />
					Advanced Settings
				</Button>
			</PopoverTrigger>
			<PopoverContent styles={[styles.content]} align='start'>
				<label className={css(styles.row)}>
					<Checkbox checked={saveAsNote} onCheckedChange={checked => onSaveAsNoteChange(checked === true)} />
					Save a copy of this email as a note
				</label>
				<label className={css(styles.row, !saveAsNote && styles.rowDisabled)}>
					<Checkbox
						checked={noteVisibility === 'all'}
						disabled={!saveAsNote}
						onCheckedChange={checked => onNoteVisibilityChange(checked === true ? 'all' : 'admin')}
					/>
					Shared note
				</label>
			</PopoverContent>
		</Popover>
	);
};

const styles = StyleSheet.create({
	trigger: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: THEME.spacing.xs,
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.light,
	},
	gear: {
		width: 14,
		height: 14,
	},
	content: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
		padding: THEME.spacing.sm,
		minWidth: 240,
		// The components-v2 Popover defaults to z-index 5 (below the composer
		// Dialog at 50); lift it so the popover renders above the modal.
		zIndex: 1000,
	},
	row: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		fontSize: THEME.typography.fontSizes.sm,
		cursor: 'pointer',
	},
	rowDisabled: {
		opacity: 0.5,
		cursor: 'default',
	},
});
